import React from 'react'

import * as c from '../../../common'

import Pad from '../../comps/Pad/Pad'
import { FormatPrice } from '../../helpers/FormatPrice/FormatPrice'

import './FinancingCalculator.css'

export interface FinancingCalculatorProps {
  alarmBill: number
  contentUser: 'ALARM' | 'SOLAR' | 'NORMAL'
  energyBill: number
  solarBill: number
}

export default React.memo<FinancingCalculatorProps>(
  function FinancingCalculator({
    alarmBill,
    contentUser,
    energyBill,
    solarBill,
  }) {
    const [paymentType, setPaymentType] =
      React.useState<PaymentType>('1to18-19to300')
    const [financeLen, setFinanceLen] = React.useState('5')
    const [financeLenSolar, setFinanceLenSolar] = React.useState('5')

    const cashYears = (cashYear: string) => {
      if (cashYear === 'Cash 5') return 5
      if (cashYear === 'Cash 10') return 10
      if (cashYear === 'Cash 15') return 15

      return Number(cashYear)
    }

    //#region financing
    const [monthlyFinancingPayment, setMonthlyFinancingPayment] =
      React.useState('')
    const yearlyFinancing = React.useMemo(() => {
      if (financeLen === 'Cash 5') {
        return new Array(cashYears(financeLen)).fill(0).map((_, i) => {
          if (i === 0) return Number(monthlyFinancingPayment)

          return 0
        })
      }
      if (financeLen === 'Cash 10') {
        return new Array(cashYears(financeLen)).fill(0).map((_, i) => {
          if (i === 0) return Number(monthlyFinancingPayment)

          return 0
        })
      }

      if (financeLen === 'Cash 15') {
        return new Array(cashYears(financeLen)).fill(0).map((_, i) => {
          if (i === 0) return Number(monthlyFinancingPayment)

          return 0
        })
      }

      return new Array(Number(financeLen)).fill(
        Number(monthlyFinancingPayment) * 12,
      )
    }, [financeLen, monthlyFinancingPayment])
    const totalFinancing = yearlyFinancing.reduce((a, b) => a + b, 0)
    //#endregion financing

    //#region alarm

    const [rateAlarm, setRateAlarm] = React.useState('2')

    const [alarmLen, setAlarmLen] = React.useState('5')

    const monthlyAlarmPayments = React.useMemo(() => {
      let array: any[] = []

      let prev = alarmBill * 12

      let rate = Number(rateAlarm)

      new Array(Number(alarmLen)).fill(0).forEach((_) => {
        array.push(Math.round(prev + prev * (rate / 100)))
        prev = Math.round(prev + prev * (rate / 100))
      })
      return array
    }, [alarmBill, alarmLen, rateAlarm])

    const alarmTotal = monthlyAlarmPayments.reduce((a, b) => a + b, 0)

    //#endregion alarm
    //#region electricity
    const [powerBill, setPowerBill] = React.useState('')
    const [powerInflation, setPowerInflation] = React.useState('5')
    const yearlyPower = React.useMemo(() => {
      const _electricPayments = [
        contentUser === 'ALARM' ? energyBill * 12 : Number(powerBill) * 12,
      ]

      let i = 0
      while (
        ++i < Number(contentUser === 'ALARM' ? alarmLen : cashYears(financeLen))
      ) {
        /** "43" -> 0.43 */
        const decimalInflation = Number(powerInflation) / 100
        const percentage = 1 + decimalInflation
        const previousYearTotal = _electricPayments[i - 1]
        const newYearsTotal = previousYearTotal! * percentage

        _electricPayments[i] = Math.round(newYearsTotal)
      }

      return _electricPayments
    }, [
      contentUser,
      energyBill,
      powerBill,
      alarmLen,
      financeLen,
      powerInflation,
    ])
    const totalPower = yearlyPower.map(Number).reduce((a, b) => a + b, 0)
    //#endregion electricity

    //#region taxCredit
    const [taxCredit, setTaxCredit] = React.useState('')

    // const focusTaxCredit = React.useCallback(
    //   (e) => {
    //     e.preventDefault()
    //     taxCreditRef.current && taxCreditRef.current.focus()
    //   },
    //   [taxCreditRef],
    // )
    // const handleTaxCreditChange = React.useCallback((e) => {
    //   setTaxCredit(e.target.value)
    // }, [])
    //#endregion taxCredit

    //#region solar alarm

    const solarAlarmPayments = React.useMemo(() => {
      return new Array(Number(alarmLen)).fill((solarBill * 12).toString())
    }, [alarmLen, solarBill])

    const solarAlarmTotal = React.useMemo(() => {
      let acc = 0

      solarAlarmPayments.forEach((value) => {
        if (typeof value === 'string') {
          acc += parseInt(value)
        }
      })

      return isNaN(acc) ? 0 : acc
    }, [solarAlarmPayments])
    //#endregion solar alarm

    const [appliesTax, setAppliesTax] = React.useState(true)

    //#region taxCreditPerYear
    const [creditPerYear, setCreditPerYear] = React.useState('')

    // const focusCreditPerYear = React.useCallback(
    //   (e) => {
    //     e.preventDefault()
    //     creditPerYearRef.current && creditPerYearRef.current.focus()
    //   },
    //   [creditPerYearRef],
    // )
    // const handleCreditPerYearChange = React.useCallback((e) => {
    //   setCreditPerYear(e.target.value)
    // }, [])
    //#endregion taxCreditPerYear

    //#region solarPayments
    const [solarFirstAmountTaxApplied, setSolarFirstAmountTaxApplied] =
      React.useState('')
    const [solarSecondAmountTaxApplied, setSolarSecondAmountTaxApplied] =
      React.useState('')
    const [solarFirstAmountTaxKept, setSolarFirstAmountTaxKept] =
      React.useState('')
    const [solarSecondAmountTaxKept, setSolarSecondAmountTaxKept] =
      React.useState('')

    const yearlySolarTaxApplied = React.useMemo(() => {
      const _yearlySolarTaxApplied = new Array(
        Number(
          financeLen.startsWith('Cash')
            ? financeLenSolar
            : cashYears(financeLen).toString(),
        ),
      ).fill(0)

      return _yearlySolarTaxApplied.map((_, i) => {
        if (paymentType === '1to18-19to300') {
          if (i === 0) {
            return Number(solarFirstAmountTaxApplied) * 12
          } else if (i === 1) {
            return (
              Number(solarFirstAmountTaxApplied) * 6 +
              Number(solarSecondAmountTaxApplied) * 6
            )
          }
          return Number(solarSecondAmountTaxApplied) * 12
        } else if (paymentType === '3to18') {
          if (i === 0) {
            return Number(solarFirstAmountTaxApplied) * 10
          } else if (i > 0 && i < 2) {
            return Number(solarFirstAmountTaxApplied) * 6
          } else {
            return 0
          }
        } else if (paymentType === '1to300') {
          return Number(solarFirstAmountTaxApplied)
        } else if (paymentType === '19to300') {
          if (i === 0) {
            return 0
          } else if (i === 1) {
            return 0 + Number(solarSecondAmountTaxApplied) * 6
          }
          return Number(solarSecondAmountTaxApplied) * 12
        } else {
          return Number(solarFirstAmountTaxApplied) * 12
        }
      })
    }, [
      financeLen,
      financeLenSolar,
      paymentType,
      solarSecondAmountTaxApplied,
      solarFirstAmountTaxApplied,
    ])

    const yearlySolarTaxKept = React.useMemo(() => {
      const _yearlySolarTaxKept = new Array(
        Number(financeLen.startsWith('Cash') ? financeLenSolar : financeLen),
      ).fill(0)

      return _yearlySolarTaxKept.map((_, i) => {
        if (paymentType === '1to18-19to300') {
          if (i === 0) {
            return Number(solarFirstAmountTaxKept) * 12
          } else if (i === 1) {
            return (
              Number(solarFirstAmountTaxKept) * 6 +
              Number(solarSecondAmountTaxKept) * 6
            )
          }
          return Number(solarSecondAmountTaxKept) * 12
        } else if (paymentType === '3to18') {
          if (i === 0) {
            return Number(solarFirstAmountTaxKept) * 10
          } else if (i > 0 && i < 2) {
            return Number(solarFirstAmountTaxKept) * 6
          } else {
            return 0
          }
        } else if (paymentType === '1to300') {
          return Number(solarFirstAmountTaxKept)
        } else if (paymentType === '19to300') {
          if (i === 0) {
            return 0
          } else if (i === 1) {
            return 0 + Number(solarSecondAmountTaxKept) * 6
          }
          return Number(solarSecondAmountTaxKept) * 12
        } else {
          return Number(solarSecondAmountTaxKept) * 12
        }
      })
    }, [
      financeLen,
      financeLenSolar,
      paymentType,
      solarSecondAmountTaxKept,
      solarFirstAmountTaxKept,
    ])

    const solarTotal = (
      appliesTax ? yearlySolarTaxApplied : yearlySolarTaxKept
    ).reduce((a, b) => a + b, 0)
    //#endregion solarPayments

    const taxCreditColumn = React.useMemo(() => {
      if (financeLen.startsWith('Cash')) {
        return []
      }
      let remainingCredit = Number(taxCredit)

      const _taxCreditColumn = new Array(Number(financeLen)).fill(0).map(() => {
        let result = 0

        if (remainingCredit <= 0) {
          result = 0
        } else if (remainingCredit > Number(creditPerYear)) {
          result = Number(creditPerYear)
        } else {
          result = remainingCredit
        }
        remainingCredit -= Number(creditPerYear)
        return result
      })
      return _taxCreditColumn
    }, [financeLen, taxCredit, creditPerYear])

    const yearCost = React.useMemo(() => {
      if (financeLen.startsWith('Cash')) {
        return []
      }
      return new Array(Number(financeLen)).fill(0).map((_, i) => {
        // yearlySolarTaxKept is an expenditure
        // taxCreditColumn is money coming in
        const result = (-yearlySolarTaxKept[i]! + taxCreditColumn[i]!) as number

        const prefix = result > 0 ? '+' : ''
        return prefix + Math.abs(result)
      })
    }, [financeLen, yearlySolarTaxKept, taxCreditColumn])

    const grandTotal = yearCost
      .map((val) => (val.startsWith('+') ? val : -val))
      .map(Number)
      .reduce((a, b) => a + b, 0)
    const comesOutAhead = grandTotal > 0
    const totalSavings = appliesTax
      ? totalFinancing + totalPower - solarTotal
      : comesOutAhead
      ? totalFinancing + totalPower + Math.abs(grandTotal)
      : totalFinancing + totalPower - Math.abs(grandTotal)

    const labelPaymentFlex = React.useMemo(() => {
      if (paymentType === '1to18-19to300') {
        return '1 - 18' as string
      } else if (paymentType === '3to18') {
        return '3-18' as string
      } else if (paymentType === '1to300') {
        return '1 - 300 ' as string
      } else {
        return 'Cash'
      }
    }, [paymentType])

    const labelPaymentTax = React.useMemo(() => {
      if (paymentType === '1to18-19to300') {
        return '19 - 300' as string
      } else if (paymentType === '3to18') {
        return '3 - 18' as string
      } else if (paymentType === '19to300') {
        return '19-300'
      } else {
        return ''
      }
    }, [paymentType])

    const handlePaymentType = React.useCallback(
      (e: { target: { value: string } }) => {
        if (e.target.value === '1to18-19to300') {
          setPaymentType('1to18-19to300')
        } else if (e.target.value === '3to18') {
          setPaymentType('3to18')
        } else if (e.target.value === '1to300') {
          setPaymentType('1to300')
        } else if (e.target.value === '19to300') {
          setPaymentType('19to300')
        } else {
          setPaymentType('Cash')
        }
      },
      [setPaymentType],
    )

    return (
      <div
        className="flex col"
        style={{
          marginTop: contentUser !== 'SOLAR' ? 100 : 30,
          paddingLeft: '40px',
        }}
      >
        <div className={contentUser === 'ALARM' ? 'lenght-alarm' : 'hide'}>
          <div className="selector-alarm">
            <span className="self-center center-text subtitle">Length</span>
            <Vert amt={4} />
            <select
              className="
                        top-border
                        bottom-border
                        round-top-border
                        round-bottom-border
                        _widget
                        select
                        bg-w
                      "
              onChange={(e) => {
                setAlarmLen(e.target.value)
              }}
              value={alarmLen}
            >
              <option value={5}>5 yrs</option>
              <option value={10}>10 yrs</option>
              <option value={15}>15 yrs</option>
              <option value={20}>20 yrs</option>
              <option value={25}>25 yrs</option>
              <option value={30}>30 yrs</option>
            </select>
          </div>
        </div>

        {contentUser !== 'SOLAR' ? (
          <>
            <div className="flex row justified">
              {contentUser === 'ALARM' && <Hor amt={50} />}
              <div className="flex col aligned relative no-wrap">
                {contentUser === 'ALARM' ? (
                  <H0 className="center-text main-header">Alarm Payment</H0>
                ) : (
                  <H0 className="center-text main-header">{`${
                    financeLen.startsWith('Cash') ? 'Cash' : 'Finance'
                  }`}</H0>
                )}

                <div className="flex row">
                  {/* ALARM / FINANCE TABLE*/}

                  {contentUser === 'ALARM' ? (
                    /* @ts-expect-error */
                    <Table
                      label="Yearly alarm payment"
                      leftSide="years"
                      rows={monthlyAlarmPayments}
                    />
                  ) : (
                    /* @ts-expect-error */
                    <Table
                      label={
                        financeLen.startsWith('Cash')
                          ? 'Cash payment'
                          : 'Yearly finance payment'
                      }
                      leftSide="years"
                      rows={yearlyFinancing}
                    />
                  )}

                  <Hor amt={24} />

                  <div className="flex col">
                    {contentUser === 'ALARM' ? (
                      <Input
                        label={
                          financeLen.startsWith('Cash') ? 'Amount' : 'Monthly'
                        }
                        onChange={() => {}}
                        type="$"
                        value={alarmBill}
                        edit={contentUser === 'ALARM'}
                      />
                    ) : (
                      <Input
                        label={
                          financeLen.startsWith('Cash') ? 'Amount' : 'Monthly'
                        }
                        onChange={setMonthlyFinancingPayment}
                        type="$"
                        value={monthlyFinancingPayment}
                      />
                    )}
                    <Pad amt={24} />
                    {contentUser === 'ALARM' ? (
                      <>
                        <Vert amt={24} />
                        <Input
                          label="Rate"
                          type="%"
                          onChange={setRateAlarm}
                          value={rateAlarm}
                          edit={contentUser === 'ALARM'}
                        />
                      </>
                    ) : (
                      <>
                        <span className="self-center center-text subtitle">
                          Length
                        </span>
                        <Vert amt={4} />
                        <select
                          className="finance-select round-bottom-border round-top-border"
                          onChange={(e) => {
                            setFinanceLen(e.target.value)
                          }}
                          value={financeLen}
                        >
                          <option value={5}>5 yrs</option>
                          <option value={10}>10 yrs</option>
                          <option value={15}>15 yrs</option>
                          <option value="Cash 5">Cash 5</option>
                          <option value="Cash 10">Cash 10</option>
                          <option value="Cash 15">Cash 15</option>
                        </select>
                      </>
                    )}
                  </div>
                </div>

                <Vert amt={24} />

                <div className="line" />

                <h1 className="center-text red-text amount-price">
                  {contentUser === 'ALARM' ? (
                    ` $${alarmTotal.toLocaleString('en')}`
                  ) : (
                    <>{`${
                      financeLen === 'Cash'
                        ? ` $${parseFloat(
                            monthlyFinancingPayment,
                          ).toLocaleString('en')}`
                        : `$${parseFloat(
                            totalFinancing.toFixed(2),
                          ).toLocaleString('en')}`
                    }`}</>
                  )}
                </h1>
              </div>

              <Hor amt={128} />

              <div className="flex col aligned relative no-wrap">
                <H0 className="center-text main-header">Electricity rates</H0>

                <div className="flex row">
                  {/* @ts-expect-error */}
                  <Table
                    label="Yearly payment"
                    leftSide="years"
                    rows={yearlyPower}
                  />

                  <Hor amt={24} />

                  <div className="flex col">
                    <Input
                      label="Monthly"
                      onChange={setPowerBill}
                      type="$"
                      value={contentUser === 'ALARM' ? energyBill : powerBill}
                      edit={contentUser === 'ALARM'}
                    />
                    <Vert amt={24} />

                    <Input
                      label="Rate"
                      type="%"
                      onChange={setPowerInflation}
                      value={powerInflation}
                      edit={contentUser === 'ALARM'}
                    />
                  </div>
                </div>

                <Vert amt={24} />

                <div className="line" />

                <h1 className="center-text red-text amount-price">
                  ${parseFloat(totalPower.toFixed(0)).toLocaleString('en')}
                </h1>
              </div>

              {contentUser === 'ALARM' && <Hor amt={128} />}

              {contentUser === 'ALARM' && (
                <>
                  <div className="flex col aligned relative no-wrap">
                    <H0 className="center-text main-header">Solar Payments</H0>

                    <div className="flex row">
                      {/* @ts-expect-error */}
                      <Table
                        label="Yearly payment"
                        leftSide="years"
                        rows={solarAlarmPayments}
                      />

                      <Hor amt={24} />
                      <div className="flex col">
                        <Input
                          label="Monthly"
                          onChange={() => {}}
                          type="$"
                          value={solarBill}
                          edit={true}
                        />
                        <Vert amt={24} />
                      </div>
                    </div>

                    <Vert amt={24} />

                    <div className="line" />

                    <h1 className="center-text green-text">
                      $
                      {parseFloat(solarAlarmTotal.toFixed(2)).toLocaleString(
                        'en',
                      )}
                    </h1>
                  </div>
                  {contentUser === 'ALARM' && <Hor amt={50} />}
                </>
              )}
            </div>

            <span className="center-text total-amt-label">Total amount:</span>
            <h2 className="center-text red-text total-amt">
              $
              {Number(monthlyFinancingPayment || '0') +
                Number(powerBill || '0')}
              /mo
            </h2>

            <h1 className="center-text red-text amount-price">
              $
              {parseFloat(
                (totalFinancing + totalPower).toFixed(2),
              ).toLocaleString('en')}
            </h1>

            {contentUser === 'ALARM' ? null : (
              <span className="red-text center-text total-amount-years">
                over the course of{' '}
                {financeLen.length === 1 ? financeLen : cashYears(financeLen)}{' '}
                years
              </span>
            )}
            <div className="divider" />
          </>
        ) : null}

        {/* SOLAR SECTION */}
        {contentUser === 'ALARM' ? null : (
          <>
            {' '}
            <H0 className="center-text margin-bottom-0">Solar</H0>
            <div className="payment-options">
              <h2 style={{ textAlign: 'center', width: '500px' }}>
                Payment Options
              </h2>
              <span>(in months)</span>

              <select
                onChange={handlePaymentType}
                className="top-border
                        bottom-border
                        round-top-border
                        round-bottom-border
                        _widget
                        select
                        bg-w"
              >
                <option value="1to18-19to300">1-18 , 19-300 </option>
                <option value="3to18">3-18</option>
                <option value="1to300">1-300 </option>
                <option value="19to300">19-300</option>
                <option value="Cash"> Cash </option>
              </select>
              <Vert amt={30} />
              <div className={financeLen === 'Cash' ? '' : 'hide'}>
                <Vert amt={10} />
                <span
                  className="self-center center-text subtitle"
                  style={{ marginLeft: 15 }}
                >
                  Length
                </span>

                <Vert amt={4} />

                <select
                  className="
    top-border
    bottom-border
    round-top-border
    round-bottom-border
    _widget
    select
    bg-w
  "
                  onChange={(e) => {
                    setFinanceLenSolar(e.target.value)
                  }}
                  value={financeLenSolar}
                >
                  <option value={5}>5 yrs</option>
                  <option value={10}>10 yrs</option>
                  <option value={15}>15 yrs</option>
                  <option value={20}>20 yrs</option>
                  <option value={25}>25 yrs</option>
                </select>
              </div>
            </div>
            <div className="flex row justified">
              <div className="flex col">
                <div className="flex row justified">
                  {appliesTax ? (
                    <div className="flex col aligned">
                      <h4 className="center-text">Solar payment</h4>

                      {/* @ts-expect-error */}
                      <Table
                        label="Yearly payment"
                        leftSide="years"
                        rows={yearlySolarTaxApplied}
                        paymentType={paymentType}
                      />
                    </div>
                  ) : (
                    <div className="flex row justified">
                      {/* @ts-expect-error */}
                      <Table
                        borderType="first"
                        label="Solar payment"
                        leftSide="years"
                        rows={yearlySolarTaxKept}
                        paymentType={paymentType}
                      />
                      {/* @ts-expect-error */}
                      <Table
                        borderType="pointy"
                        label="Tax credit"
                        leftSide="-"
                        rows={taxCreditColumn}
                        leftMargin
                        paymentType={paymentType}
                      />

                      <div
                        className="flex col margin-left-36"
                        style={{ width: '84px' }}
                      >
                        {/* @ts-expect-error */}
                        <Table
                          borderType="last"
                          label="Sum"
                          leftSide="="
                          positiveValuesGreen
                          rows={yearCost}
                          paymentType={paymentType}
                        />

                        <Vert amt={financeLen.startsWith('Cash') ? 170 : 24} />

                        {/* <div className={comesOutAhead ? 'green-line' : 'red-line'} /> */}

                        {/* <h1
                          className={
                            solarSecondAmountTaxKept && solarFirstAmountTaxKept
                              ? `
              center-text
              ${
                comesOutAhead
                  ? 'black-text center-text opacity-1'
                  : 'black-text center-text opacity-1'
              }
            `
                              : 'opacity-0'
                          }
                        >
                          {FormatPrice(
                            grandTotal < 0 ? grandTotal * -1 : grandTotal,
                            '$',
                          )}
                        </h1> */}
                      </div>
                    </div>
                  )}

                  <Hor amt={32} />

                  <div className="flex col">
                    {/* Not sure why this phantom heading is only needed in one case. */}

                    <h4 className="center-text opacity-0">.</h4>

                    {paymentType === '19to300' ? null : (
                      <>
                        <Input
                          label={labelPaymentFlex}
                          type="$"
                          onChange={
                            appliesTax
                              ? setSolarFirstAmountTaxApplied
                              : setSolarFirstAmountTaxKept
                          }
                          value={
                            appliesTax
                              ? solarFirstAmountTaxApplied
                              : solarFirstAmountTaxKept
                          }
                        />
                      </>
                    )}
                    {paymentType === '1to300' ||
                    paymentType === 'Cash' ||
                    paymentType === '3to18' ? null : (
                      <>
                        {' '}
                        <Vert amt={24} />
                        <Input
                          label={labelPaymentTax}
                          type="$"
                          onChange={
                            appliesTax
                              ? setSolarSecondAmountTaxApplied
                              : setSolarSecondAmountTaxKept
                          }
                          value={
                            appliesTax
                              ? solarSecondAmountTaxApplied
                              : solarSecondAmountTaxKept
                          }
                        />
                      </>
                    )}
                  </div>
                </div>

                <React.Fragment>
                  <Vert amt={24} />

                  <div className="line" />

                  <h1
                    className={
                      solarSecondAmountTaxApplied && solarFirstAmountTaxApplied
                        ? 'center-text red-text opacity-1'
                        : 'opacity-0'
                    }
                  >
                    {FormatPrice(solarTotal as number, '$')}
                  </h1>
                </React.Fragment>
              </div>

              <Hor amt={60} />

              <div className="flex col max-w">
                <h4 className="center-text">Tax credit amount</h4>

                <div style={{ alignSelf: 'center' }}>
                  <Input
                    label={''}
                    type="$"
                    onChange={setTaxCredit}
                    value={taxCredit}
                  />
                </div>

                <span className="small-text center-text darker-text">
                  Click to edit
                </span>

                {!appliesTax && (
                  <React.Fragment>
                    <Vert amt={12} />

                    <h4 className="center-text">Amount per year</h4>

                    <div style={{ alignSelf: 'center' }}>
                      <Input
                        label={''}
                        type="$"
                        onChange={setCreditPerYear}
                        value={creditPerYear}
                      />
                    </div>

                    <span className="small-text center-text darker-text">
                      Click to edit
                    </span>
                  </React.Fragment>
                )}

                <Vert amt={12} />

                <div className="flex col aligned">
                  <div className="flex col">
                    <span
                      className={`
                  center-text
                  top-border
                  round-top-border
                  toggle-text
                  side-border
                  ${appliesTax && 'toggle-text-active'}
                  ${appliesTax && 'pri-border'}
                `}
                      onClick={() => setAppliesTax(true)}
                    >
                      Apply tax credit
                    </span>

                    <span
                      className={`
                center-text
                bottom-border
                round-bottom-border
                toggle-text
                side-border
                ${!appliesTax && 'toggle-text-active pri-border'}
              `}
                      onClick={() => setAppliesTax(false)}
                    >
                      Keep tax credit
                    </span>
                  </div>

                  <Vert amt={24} />

                  <span className="center-text pri-text credit-explanation">
                    {appliesTax
                      ? 'Applies tax credit directly to monthly payments over the course of 25 years'
                      : 'You pocket the tax credit and apply that cash to the tax credit'}
                  </span>
                </div>
              </div>
            </div>
            {/* SOLAR SECTION END */}
            {contentUser !== 'SOLAR' ? (
              <>
                <div className="divider" />

                <H0 className="center-text ">Total Saving</H0>
                <h1
                  className={
                    appliesTax && totalFinancing && totalPower && solarTotal
                      ? 'center-text opacity-1'
                      : !appliesTax &&
                        totalFinancing &&
                        totalPower &&
                        grandTotal
                      ? 'center-text opacity-1'
                      : 'opacity-0'
                  }
                  style={{
                    color: totalSavings > 0 ? 'green' : 'red',
                    marginBottom: 0,
                    fontSize: '24px',
                  }}
                >
                  {totalSavings > 0
                    ? `+ $${totalSavings.toLocaleString('en')}`
                    : `- ${Math.abs(totalSavings).toLocaleString('en')}`}
                </h1>
                <span className="green-text align-center over-years-text">
                  Over the course of {financeLen} years
                </span>
              </>
            ) : null}
          </>
        )}
      </div>
    )
  },
)

//const EMPTY_FN = () => {}

const Vert: React.FC<{ amt: number }> = React.memo(({ amt }) => (
  <div style={{ height: amt }} />
))

const Hor: React.FC<{ amt: number }> = React.memo(({ amt }) => (
  <div style={{ width: amt }} />
))

const H0: React.FC<
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLHeadingElement>,
    HTMLHeadingElement
  >
> = React.memo((props) => (
  <h1 className={props.className + ' h-zero'}>{props.children}</h1>
))

interface TableProps {
  borderType: 'default' | 'first' | 'last' | 'pointy'
  label: string
  leftMargin: boolean
  leftSide: 'years' | '-' | '='
  positiveValuesGreen: boolean
  rows: number[] | string[]
  paymentType: PaymentType
}

const Table = React.memo(
  ({
    borderType = 'default',
    label,
    leftMargin = false,
    leftSide = 'years',
    positiveValuesGreen = false,
    rows,
    paymentType,
  }: TableProps) => {
    const getSumRows = React.useCallback((): number => {
      return rows.reduce<number>(
        (acc, item): number => c.parseFloating(acc) + c.parseFloating(item),
        0,
      )
    }, [rows])

    return (
      <div
        className={`
            flex
            col
            table
            ${leftMargin && 'margin-left-36'}
        `}
      >
        <span className="self-center center-text subtitle">{label}</span>

        <Vert amt={4} />

        {paymentType === 'Cash' ? (
          <div
            className="relative"
            style={{ width: '101px', marginTop: '0px' }}
          >
            {leftSide === 'years' && (
              <span className="center-text label label-pri-text">
                {new Date().getUTCFullYear()}
              </span>
            )}

            {leftSide === '-' && <span className="label">-</span>}

            {leftSide === '=' && <span className="label">=</span>}

            <div
              style={{ width: '101px' }}
              className={`
                      flex
                      row
                      side-border
                      bottom-border
                      bg-w
                      'top-border'
                      ${borderType === 'default' && 'round-top-border'}
                      'round-top-right'
                      ${borderType === 'default' && 'round-bottom-border'}
                      ${borderType === 'first' && 'round-top-left'}
                      ${borderType === 'first' && 'round-bottom-left'}
                      ${borderType === 'last' && 'round-bottom-right'}
                      top-border
                    `}
            >
              <div className="right-border relative table-symbol">
                <span className="absolutely-centered bit-bigger-text">$</span>
              </div>

              <input
                className={`
                        input
                        input-long
                        small-text
                        ${positiveValuesGreen && 'green-text'}
                      `}
                disabled
                inputMode="numeric"
                size={12}
                type="text"
                value={getSumRows()}
              />
            </div>
          </div>
        ) : (
          rows.map((row, i) => (
            <div
              className="relative"
              key={i}
              style={{ width: '101px', height: '33.99px' }}
            >
              {leftSide === 'years' && (
                <span className="center-text label label-pri-text">
                  {new Date().getUTCFullYear() + i}
                </span>
              )}

              {leftSide === '-' && <span className="label">-</span>}

              {leftSide === '=' && <span className="label">=</span>}

              <div
                style={{ width: '101px' }}
                className={`
                      flex
                      row
                      side-border
                      bottom-border
                      bg-w
                      ${i === 0 && 'top-border'}
                      ${
                        i === 0 &&
                        borderType === 'default' &&
                        'round-top-border'
                      }
                      ${i === 0 && borderType === 'last' && 'round-top-right'}
                      ${
                        i === rows.length - 1 &&
                        borderType === 'default' &&
                        'round-bottom-border'
                      }
                      ${borderType === 'first' && i === 0 && 'round-top-left'}
                      ${
                        borderType === 'first' &&
                        i === rows.length - 1 &&
                        'round-bottom-left'
                      }
                      ${
                        borderType === 'last' &&
                        i === rows.length - 1 &&
                        'round-bottom-right'
                      }
                    `}
              >
                <div className="right-border relative table-symbol">
                  <span className="absolutely-centered bit-bigger-text">$</span>
                </div>

                <input
                  className={`
                        input
                        input-long
                        small-text
                        ${
                          typeof row === 'string' &&
                          positiveValuesGreen &&
                          row.indexOf('+') === 0 &&
                          'green-text'
                        }
                      `}
                  disabled
                  inputMode="numeric"
                  size={12}
                  type="text"
                  value={row}
                />
              </div>
            </div>
          ))
        )}
      </div>
    )
  },
)

interface InputProps {
  label: string
  onChange: (text: string) => void
  type: '$' | '%'
  value: string | number
  edit?: boolean
}

const Input = React.memo(
  ({ label, onChange, type, value, edit }: InputProps) => {
    const [focused, setFocused] = React.useState(false)
    const didFocus = React.useCallback(() => {
      setFocused(true)
    }, [])
    const didBlur = React.useCallback(() => {
      setFocused(false)
    }, [])

    return (
      <div className="flex col" style={{ width: '101px' }}>
        <span className="center-text subtitle">{label}</span>

        <Vert amt={4} />

        <div
          className={`
                flex
                row
                ${focused ? 'top-border-orange' : 'top-border'}
                ${focused ? 'bottom-border-orange' : 'bottom-border'}
                round-top-border
                round-bottom-border
                ${focused ? 'side-border-orange' : 'side-border'}
                bg-w
              `}
        >
          {type === '$' && (
            <div className="right-border relative input-symbol">
              <span className="absolutely-centered bit-bigger-text">$</span>
            </div>
          )}

          <input
            className="input input-short bit-bigger-text"
            inputMode="numeric"
            onBlur={didBlur}
            onChange={React.useCallback(
              (e: { target: { value: string } }) => {
                onChange(e.target.value)
              },
              [onChange],
            )}
            onFocus={didFocus}
            placeholder="123.45"
            size={6}
            type="text"
            value={value}
            disabled={edit ? edit : false}
          />

          {type === '%' && (
            <div className="left-border relative input-symbol">
              <span className="absolutely-centered bit-bigger-text">%</span>
            </div>
          )}
        </div>
      </div>
    )
  },
)

type PaymentType = '1to18-19to300' | '3to18' | '1to300' | '19to300' | 'Cash'
